import { Check, Plus, RotateCcw } from 'lucide-react'
import { useState } from 'react'
import { useSetLibrarySaved } from '@/api/hooks'
import type { QueueItem } from '@/audio/store'
import { IconButton } from '@/components/ui'
import { describeError } from '@/features/search/describeError'
import { cn } from '@/lib/cn'
import { useCurrentSong } from './useCurrentSong'

export function CurrentSongActions({ item, active }: { item: QueueItem; active: boolean }) {
  const current = useCurrentSong(item, active)
  const membership = useSetLibrarySaved()
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState('')
  const saved = current.data?.is_saved ?? false
  const retry = current.isError && !current.data
  const label = retry ? 'Retry loading library status' : saved ? `Remove ${item.title} from library` : `Add ${item.title} to library`
  return <div className="relative shrink-0" data-gesture-ignore>
    <IconButton
      size="lg" round aria-label={label} title={saved ? 'Remove from library, playlists and favourites' : label} aria-pressed={saved}
      className={cn(saved && 'text-accent disabled:opacity-100')}
      disabled={!current.data && !retry}
      loading={membership.isPending || current.isPending}
      onClick={() => {
        if (retry) { void current.refetch(); return }
        if (!current.data || membership.isPending) return
        setError(null)
        setNotice('')
        membership.mutate({ id: item.libraryItemId, saved: !saved }, {
          onSuccess: (song) => setNotice(song.is_saved ? 'Added to library.' : 'Removed from library, playlists and favourites.'),
          onError: (failure) => setError(describeError(failure, 'Could not update this song. Tap again to retry.')),
        })
      }}
    >
      {retry ? <RotateCcw className="h-6 w-6" aria-hidden="true" /> : saved ? <Check className="h-6 w-6" aria-hidden="true" /> : <Plus className="h-6 w-6" aria-hidden="true" />}
    </IconButton>
    {error ? <p role="alert" className="absolute right-0 top-full z-10 w-64 rounded-lg border border-border bg-surface-raised p-3 text-sm text-danger shadow-lg">{error}</p> : null}
    <p role="status" className="sr-only">{notice}</p>
  </div>
}
